import React, { useState, useEffect, useRef } from "react";
import { Formik } from "formik";
import * as yup from "yup";
import Modal from "../../../../components/modal";
import Select from "../../../../components/form/select";
import TextInput from "../../../../components/form/text-input";
import TextArea from "../../../../components/form/text-area";
import TagInput from "../../../../components/form/tag-input";
import Button from "../../../../components/new-button";
import { saveAnnouncement, updateAnnouncement, SearchOrganizations, SearchOrganizationMembers, SearchUsers } from "../../../../../api";
import { toast } from "react-hot-toast";
import useUserStore from "../../../../stores/user";
import CalendarIcon from "../../../../assets/svg/icons/common/Calender";
import styles from "./styles.module.scss";

const getCategoryType = (category) => {
  const categoryLower = category?.toLowerCase() || "";
  if (categoryLower.includes("release")) return "success";
  if (categoryLower.includes("alert")) return "danger";
  if (categoryLower.includes("bug") || categoryLower.includes("fix")) return "info";
  return "success";
};

const CategoryOption = ({ option, selected }) => {
  const categoryType = getCategoryType(option.value);
  const dotColor = categoryType === "success" ? "#047d1c" : categoryType === "danger" ? "#a01704" : "#0e33b5";
  const bgColor = categoryType === "success" ? "#d6f6dc" : categoryType === "danger" ? "#ffe7e4" : "#dee6ff";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <div
        style={{
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          backgroundColor: dotColor,
          flexShrink: 0,
        }}
      />
      <span>{option.label}</span>
    </div>
  );
};

const schema = yup.object({
  category: yup.string().required("* Category is required"),
  title: yup.string().required("* Title is required").max(255, "* Title must be 255 characters or less"),
  description: yup.string().nullable(),
  target_type: yup.string().required("* Target type is required"),
  starts_at: yup.string().nullable(),
  expires_at: yup.string().nullable().test(
    "is-after-start",
    "* Expiry date must be after or equal to start date",
    function(value) {
      const { starts_at } = this.parent;
      if (!value || !starts_at) return true;
      return new Date(value) >= new Date(starts_at);
    }
  ),
  organization_ids: yup.array().when("target_type", {
    is: "organization",
    then: (schema) => schema.min(1, "* At least one organization is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  user_ids: yup.array().when("target_type", {
    is: "user",
    then: (schema) => schema.min(1, "* At least one user is required"),
    otherwise: (schema) => schema.nullable(),
  }),
});

const UserSearchField = ({ 
  value, 
  onChange, 
  error, 
  errored, 
  touched, 
  userOptions, 
  onSearchChange, 
  loading,
  isPCAdmin,
  user
}) => {
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (!hasSearched) {
      if (isPCAdmin) {
        onSearchChange("");
        setHasSearched(true);
      } else if (!isPCAdmin && user?.organization_id) {
        onSearchChange("");
        setHasSearched(true);
      }
    }
  }, [isPCAdmin, user?.organization_id, hasSearched, onSearchChange]);

  return (
    <TagInput
      id="user_ids"
      label="Users"
      value={value || []}
      onChange={onChange}
      error={error}
      errored={errored}
      touched={touched}
      placeholder="Type to search users"
      options={userOptions}
      search
      async
      onSearchChange={onSearchChange}
      loading={loading}
    />
  );
};

const CreateAnnouncementModal = ({ open, onClose, onSuccess, announcement = null, isEdit = false }) => {
  const [submitting, setSubmitting] = useState(false);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [organizationSearchLoading, setOrganizationSearchLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const { isPCAdmin, user } = useUserStore();
  const organizationSearchTimeoutRef = useRef(null);
  const userSearchTimeoutRef = useRef(null);

  useEffect(() => {
    if (open) {
      fetchOrganizations("");
      if (isEdit && announcement) {
        if (announcement.target_type === "organization" && announcement.targets) {
          announcement.targets.forEach(target => {
            if (target.organization) {
              setOrganizationOptions(prev => {
                const exists = prev.some(opt => opt.value === target.organization.id);
                if (!exists) {
                  return [...prev, {
                    value: target.organization.id,
                    label: target.organization.name
                  }];
                }
                return prev;
              });
            }
          });
        }
        if (announcement.target_type === "user" && announcement.targets) {
          announcement.targets.forEach(target => {
            if (target.user) {
              setUserOptions(prev => {
                const exists = prev.some(opt => opt.value === target.user.id);
                if (!exists) {
                  return [...prev, {
                    value: target.user.id,
                    label: `${target.user.name}${target.user.email ? ` (${target.user.email})` : ""}${target.user.organization_name ? ` - ${target.user.organization_name}` : ""}`
                  }];
                }
                return prev;
              });
            }
          });
        }
      }
    } else {
      setOrganizationOptions([]);
      setUserOptions([]);
      if (organizationSearchTimeoutRef.current) {
        clearTimeout(organizationSearchTimeoutRef.current);
      }
      if (userSearchTimeoutRef.current) {
        clearTimeout(userSearchTimeoutRef.current);
      }
    }
  }, [open, isEdit, announcement]);

  const fetchOrganizations = async (searchText = "") => {
    try {
      setOrganizationSearchLoading(true);
      const response = await SearchOrganizations({ term: searchText });
      if (response?.success && response?.data) {
        const orgs = response.data.map((org) => ({
          value: org.id,
          label: org.name,
        }));
        setOrganizationOptions(orgs);
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
      setOrganizationOptions([]);
    } finally {
      setOrganizationSearchLoading(false);
    }
  };

  const handleOrganizationSearch = (searchText) => {
    if (organizationSearchTimeoutRef.current) {
      clearTimeout(organizationSearchTimeoutRef.current);
    }

    organizationSearchTimeoutRef.current = setTimeout(() => {
      fetchOrganizations(searchText);
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (organizationSearchTimeoutRef.current) {
        clearTimeout(organizationSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleDateChange = (e, type, setFieldValue, setFieldError, values) => {
    const value = e.target.value;
    const today = new Date().toISOString().split('T')[0];

    if (type === "from") {
      if (value > today) {
        setFieldValue("starts_at", value);
        setFieldError("starts_at", "From date cannot be a future date.");
        return;
      }
      if (values.expires_at && value > values.expires_at) {
        setFieldError("starts_at", "Start date cannot be later than expiry date.");
      } else {
        setFieldError("starts_at", undefined);
      }
      setFieldValue("starts_at", value);
    } else {
      if (values.starts_at && value < values.starts_at) {
        setFieldError("expires_at", "Expiry date cannot be earlier than start date.");
      } else {
        setFieldError("expires_at", undefined);
      }
      setFieldValue("expires_at", value);
    }
  };

  const fetchUsers = async (searchText = "") => {
    try {
      setUserSearchLoading(true);
      const currentUserOrgId = user?.organization_id;

      if (isPCAdmin) {
        const response = await SearchUsers({ q: searchText });
        if (response?.success && response?.users) {
          const userOptions = response.users.map((user) => ({
            value: user.id,
            label: `${user.name}${user.email ? ` (${user.email})` : ""}${user.organization_name ? ` - ${user.organization_name}` : ""}`,
          }));
          setUserOptions(userOptions);
        } else {
          setUserOptions([]);
        }
      } else {
        if (currentUserOrgId) {
          const response = await SearchOrganizationMembers(currentUserOrgId, { q: searchText || "" });
          if (response?.success && response?.users && response.users.length > 0) {
            const userOptions = response.users.map((user) => ({
              value: user.id,
              label: `${user.name}${user.email ? ` (${user.email})` : ""}`,
            }));
            setUserOptions(userOptions);
          } else {
            setUserOptions([]);
          }
        } else {
          setUserOptions([]);
        }
      }
    } catch (error) {
      console.error("Error searching users:", error);
      setUserOptions([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleUserSearch = (searchText) => {
    if (userSearchTimeoutRef.current) {
      clearTimeout(userSearchTimeoutRef.current);
    }

    userSearchTimeoutRef.current = setTimeout(() => {
      fetchUsers(searchText);
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (userSearchTimeoutRef.current) {
        clearTimeout(userSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      setSubmitting(true);
      
      const formData = {
        category: values.category,
        title: values.title?.trim() || "",
        description: values.description || null,
        target_type: values.target_type,
      };

      if (values.starts_at) {
        formData.starts_at = values.starts_at;
      }

      if (values.expires_at) {
        formData.expires_at = values.expires_at;
      }

      if (values.target_type === "organization") {
        const orgIds = (values.organization_ids || []).map(org => org.value || org).filter(id => id != null);
        formData.organization_ids = orgIds;
      }

      if (values.target_type === "user") {
        const userIds = (values.user_ids || []).map(user => user.value || user).filter(id => id != null);
        formData.user_ids = userIds;
      }

      const response = isEdit && announcement
        ? await updateAnnouncement(announcement.id, formData)
        : await saveAnnouncement(formData);

      if (response?.success) {
        toast.success(isEdit ? "Announcement updated successfully" : "Announcement created successfully");
        onSuccess?.();
        onClose();
      } else {
        toast.error(response?.message || (isEdit ? "Failed to update announcement" : "Failed to create announcement"));
        if (response?.errors) {
          setErrors(response.errors);
        }
      }
    } catch (error) {
      console.error("Error creating announcement:", error);
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
      toast.error(error?.response?.data?.message || (isEdit ? "Failed to update announcement" : "Failed to create announcement"));
    } finally {
      setSubmitting(false);
    }
  };

  const categoryOptions = [
    { value: "Release", label: "Release" },
    { value: "Alert", label: "Alert" },
    { value: "Bug Fix", label: "Bug Fix" },
  ];

  const targetTypeOptions = [
    { value: "all", label: "All Users" },
    { value: "organization", label: "Specific Organizations" },
    { value: "user", label: "Specific Users" },
  ];

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      header={isEdit ? "Edit Announcement" : "Create Announcement"}
      size="md"
    >
      <Formik
        initialValues={{
          category: isEdit && announcement ? announcement.category : "",
          title: isEdit && announcement ? announcement.title : "",
          description: isEdit && announcement ? announcement.description || "" : "",
          target_type: isEdit && announcement ? announcement.target_type : "all",
          starts_at: isEdit && announcement && announcement.starts_at ? announcement.starts_at.split('T')[0] : "",
          expires_at: isEdit && announcement && announcement.expires_at ? announcement.expires_at.split('T')[0] : "",
          organization_ids: isEdit && announcement && announcement.target_type === "organization" && announcement.targets
            ? announcement.targets
                .filter(t => t.organization_id)
                .map(t => ({ value: t.organization_id, label: t.organization?.name || `Organization ${t.organization_id}` }))
            : [],
          user_ids: isEdit && announcement && announcement.target_type === "user" && announcement.targets
            ? announcement.targets
                .filter(t => t.user_id)
                .map(t => ({ value: t.user_id, label: `${t.user?.name || `User ${t.user_id}`}${t.user?.email ? ` (${t.user.email})` : ""}` }))
            : [],
        }}
        enableReinitialize={true}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          setFieldError,
        }) => (
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formContent}>
              <div className={styles.formGroup}>
                <Select
                  label="Category"
                  name="category"
                  id="category"
                  value={values.category}
                  onChange={(option) => {
                    const value = typeof option === "object" && option !== null ? option.value : option;
                    setFieldValue("category", value || "");
                  }}
                  onBlur={handleBlur}
                  error={touched.category && errors.category}
                  errored={touched.category && errors.category}
                  touched={touched.category}
                  placeholder="Select category"
                  options={categoryOptions}
                  renderOption={(option, selected) => (
                    <CategoryOption option={option} selected={selected} />
                  )}
                />
              </div>

              <div className={styles.formGroup}>
                <Select
                  label="Target Type"
                  name="target_type"
                  id="target_type"
                  value={values.target_type}
                  onChange={(option) => {
                    const value = typeof option === "object" && option !== null ? option.value : option;
                    if (value) {
                      setFieldValue("target_type", value);
                    setFieldValue("organization_ids", []);
                    setFieldValue("user_ids", []);
                    }
                  }}
                  onBlur={handleBlur}
                  error={touched.target_type && errors.target_type}
                  errored={touched.target_type && errors.target_type}
                  touched={touched.target_type}
                  placeholder="Select target type"
                  options={targetTypeOptions}
                />
              </div>

              <div className={`${styles.formGroup} ${styles.formGroupFullWidth}`}>
                <TextInput
                  label="Title"
                  id="title"
                  name="title"
                  value={values.title}
                  onChange={handleChange}
                  onBlur={(e) => {
                    const trimmedValue = e.target.value.trim();
                    setFieldValue("title", trimmedValue);
                    handleBlur(e);
                  }}
                  error={touched.title && errors.title}
                  errored={touched.title && errors.title}
                  touched={touched.title}
                  placeholder="Enter announcement title"
                />
              </div>

              <div className={`${styles.formGroup} ${styles.formGroupFullWidth}`}>
                <TextArea
                  label="Description"
                  id="description"
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.description && errors.description}
                  errored={touched.description && errors.description}
                  touched={touched.description}
                  placeholder="Enter announcement description"
                  rows={4}
                />
              </div>

              {values.target_type === "organization" && (
                <div className={`${styles.formGroup} ${styles.formGroupFullWidth}`}>
                  <TagInput
                    id="organization_ids"
                    label="Organizations"
                    value={values.organization_ids || []}
                    onChange={(options) => {
                      setFieldValue("organization_ids", options || []);
                    }}
                    error={touched.organization_ids && errors.organization_ids}
                    errored={touched.organization_ids && errors.organization_ids}
                    touched={touched.organization_ids}
                    placeholder="Search and select organizations"
                    options={organizationOptions}
                    search
                    async
                    onSearchChange={handleOrganizationSearch}
                    loading={organizationSearchLoading}
                  />
                </div>
              )}

              {values.target_type === "user" && (
                <div className={`${styles.formGroup} ${styles.formGroupFullWidth}`}>
                  <UserSearchField
                    value={values.user_ids || []}
                    onChange={(options) => {
                      setFieldValue("user_ids", options || []);
                    }}
                    error={touched.user_ids && errors.user_ids}
                    errored={touched.user_ids && errors.user_ids}
                    touched={touched.user_ids}
                    userOptions={userOptions}
                    onSearchChange={handleUserSearch}
                    loading={userSearchLoading}
                    isPCAdmin={isPCAdmin}
                    user={user}
                  />
                </div>
              )}

              <div className={`${styles.formGroup} ${styles.formGroupFullWidth}`}>
                <div className={styles.dateFields}>
                  <div className={styles.dateField}>
                    <label className={styles.dateLabel}>From Date (Optional)</label>
                    <div className={styles.dateInputWrapper}>
                    <span className={styles.dateIcon}>
                      <CalendarIcon />
                    </span>
                    <input
                      type="date"
                      name="starts_at"
                      value={values.starts_at || ""}
                      max={new Date().toISOString().split('T')[0]}
                      onChange={(e) => handleDateChange(e, "from", setFieldValue, setFieldError, values)}
                      onBlur={handleBlur}
                      placeholder="Start date"
                    />
                    </div>
                    {touched.starts_at && errors.starts_at && (
                      <p className={styles.dateError}>{errors.starts_at}</p>
                    )}
                  </div>
                  <div className={styles.dateField}>
                    <label className={styles.dateLabel}>To Date (Optional)</label>
                    <div className={styles.dateInputWrapper}>
                    <span className={styles.dateIcon}>
                      <CalendarIcon />
                    </span>
                    <input
                      type="date"
                      name="expires_at"
                      value={values.expires_at || ""}
                      min={values.starts_at || undefined}
                      onChange={(e) => handleDateChange(e, "to", setFieldValue, setFieldError, values)}
                      onBlur={handleBlur}
                      placeholder="Expiry date"
                    />
                  </div>
                {touched.expires_at && errors.expires_at && (
                  <p className={styles.dateError}>{errors.expires_at}</p>
                )}
                  </div>
                </div>
              </div>

              <div className={styles.actions}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (isEdit ? "Updating..." : "Creating...") : (isEdit ? "Update" : "Create")}
                </Button>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default CreateAnnouncementModal;

